<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watchEffect } from 'vue'
import { useI18n } from 'vue-i18n'
import { ConnectError } from '@connectrpc/connect'
import { loadConfig } from './api/config'
import { getCustomerClient } from './api/client'
import { useFormState } from './composables/useFormState'
import { useIframeBridge } from './composables/useIframeBridge'
import { useLocale } from './composables/useLocale'
import { useSubmit } from './composables/useSubmit'
import { getFormById } from './forms'
import FormRenderer from './components/FormRenderer.vue'
import HoneypotField from './components/HoneypotField.vue'
import TurnstileWidget from './components/TurnstileWidget.vue'

const apiUrl = ref<string | null>(null)
const turnstileSiteKey = ref<string | null>(null)
const configError = ref<string | null>(null)

const locale = useLocale()
const { t } = useI18n()

const queryParams = new URLSearchParams(window.location.search)
const formId = queryParams.get('form')
const form = getFormById(formId)
const unknownForm = !form
const showDevBar = queryParams.get('dev') === '1'

// Drive theme overrides in src/styles/themes.css. Set before mount so the
// initial paint uses the right styling instead of flashing the default theme.
if (form) document.documentElement.dataset.form = form.id

watchEffect(() => {
  document.title = t('title')
  document.documentElement.lang = locale.value
})

const { values, errors, isVisible, validate, reset } = useFormState(
  form ?? getFormById('b2cContact')!,
)
const {
  honeypot,
  turnstileToken,
  state,
  errorKind,
  errorDetail,
  spamFields,
  reset: resetSubmit,
} = useSubmit()
const { requestScroll } = useIframeBridge()

const localizedErrors = computed<Record<string, string>>(() => {
  const out: Record<string, string> = {}
  for (const [k, v] of Object.entries(errors.value)) {
    out[k] = t(`errors.${v}`)
  }
  return out
})

const errorBanner = computed(() => {
  if (errorKind.value === 'network') return t('errorBanner.network')
  if (errorKind.value === 'server') return t('errorBanner.server')
  return null
})

onMounted(async () => {
  try {
    const config = await loadConfig()
    apiUrl.value = config.apiUrl
    turnstileSiteKey.value = config.turnstileSiteKey
  } catch (e) {
    configError.value = e instanceof Error ? e.message : String(e)
  }
})

async function onSubmit() {
  // Guard against double-submit. Vue reactivity disables the button on
  // state=submitting, but rapid double-click or Enter+click can squeeze a
  // second event through before the prop propagates. This is the backstop.
  if (state.value !== 'idle') return
  resetSubmit()
  if (!validate()) return
  if (!form) return

  state.value = 'submitting'
  try {
    const client = await getCustomerClient()

    // Build payload: German labels as keys (backend recipient reads German),
    // skip empty fields (recipient shouldn't have to scroll past 30 blanks).
    // System fields keep their machine-name keys because the Go handler reads
    // them ("email", "language", "_hp", "_dt", "_turnstile"). Form variant is
    // sent via the dedicated proto field formType, not the values list. For
    // email specifically: send key="email" for the auto-reply extraction path
    // AND key="E-Mail-Adresse" so the recipient sees who submitted.
    const contactFormValues: { key: string; value: string }[] = []
    const isFilled = (v: unknown): boolean => {
      if (v === null || v === undefined) return false
      if (Array.isArray(v)) return v.length > 0
      if (typeof v === 'boolean') return v === true
      return typeof v === 'string' && v.trim() !== ''
    }
    const serialize = (v: unknown): string => {
      if (Array.isArray(v)) return v.join(', ')
      if (typeof v === 'boolean') return v ? 'Ja' : 'Nein'
      return String(v ?? '')
    }
    for (const field of form.fields) {
      if (field.type === 'section') continue // display-only heading, no value
      const v = values[field.id]
      if (!isFilled(v)) continue
      const value = serialize(v)
      if (field.id === 'email') {
        contactFormValues.push({ key: 'email', value })
        contactFormValues.push({ key: field.label.de, value })
      } else {
        contactFormValues.push({ key: field.label.de, value })
      }
    }
    if (isFilled(values[form.consentField.id])) {
      contactFormValues.push({ key: form.consentField.label.de, value: 'Ja' })
    }
    contactFormValues.push({ key: 'language', value: locale.value })
    contactFormValues.push(...spamFields())

    const response = await client.submitContactForm({ formType: form.id, contactFormValues })
    if (response.success) {
      state.value = 'submitted'
      reset()
      // Long forms (e.g. becomePartner) collapse to a small success message;
      // ask the host to align the iframe top with the viewport so the user
      // sees the message instead of the now-empty space below it. nextTick
      // lets the resize observer post the new height first.
      await nextTick()
      requestScroll('top')
    } else {
      errorKind.value = 'server'
      state.value = 'idle'
      await nextTick()
      requestScroll('bottom')
    }
  } catch (e) {
    if (e instanceof ConnectError) {
      errorKind.value = 'server'
      errorDetail.value = e.message
    } else {
      errorKind.value = 'network'
      errorDetail.value = e instanceof Error ? e.message : String(e)
    }
    state.value = 'idle'
    await nextTick()
    requestScroll('bottom')
  }
}
</script>

<template>
  <main class="app">
    <header v-if="showDevBar" class="topbar">
      <small v-if="configError" class="error">Konfiguration: {{ configError }}</small>
      <small v-else-if="apiUrl">API: {{ apiUrl }}</small>
      <label class="locale">
        <span>Sprache:</span>
        <select v-model="locale">
          <option value="de">Deutsch</option>
          <option value="en">English</option>
        </select>
      </label>
    </header>

    <p v-if="unknownForm" class="error">
      Unbekanntes Formular: {{ formId }}. Verfügbare Formulare: <code>?form=b2cContact</code>,
      <code>?form=becomePartner</code>, <code>?form=device</code>,
      <code>?form=instituteSearch</code>,
      <code>?form=plcc</code>
    </p>

    <template v-else-if="form && apiUrl && turnstileSiteKey">
      <p v-if="state === 'submitted'" class="ok">{{ form.successMessage[locale] }}</p>
      <FormRenderer
        v-else
        :config="form"
        :locale="locale"
        :values="values"
        :errors="localizedErrors"
        :is-visible="isVisible"
        :data-submitting="state === 'submitting' ? 'true' : 'false'"
        @submit="onSubmit"
      >
        <template #below-fields>
          <HoneypotField v-model="honeypot" />
          <p class="spam-protection-label">{{ t('spamProtection') }}</p>
          <TurnstileWidget
            :sitekey="turnstileSiteKey"
            :locale="locale"
            @token="turnstileToken = $event"
            @expired="turnstileToken = ''"
            @error="turnstileToken = ''"
          />
        </template>
      </FormRenderer>
      <p v-if="errorBanner" class="error" role="alert">
        {{ errorBanner }}
        <small v-if="showDevBar && errorDetail"> ({{ errorDetail }})</small>
      </p>
    </template>
  </main>
</template>

<style scoped>
.app {
  max-width: var(--form-max-width);
  margin: 0 auto;
  padding: var(--space-7) var(--form-padding-x);
  position: relative;
}
.topbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: var(--font-size-xs);
  color: var(--color-text-muted);
  margin-bottom: var(--space-6);
  padding-bottom: var(--space-3);
  border-bottom: 1px solid var(--color-border);
  letter-spacing: var(--letter-spacing-wide);
  text-transform: uppercase;
}
.locale {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
}
.error {
  color: var(--color-error);
  margin-top: var(--space-4);
}
.spam-protection-label {
  margin: var(--space-4) 0 calc(-1 * var(--space-2));
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
  letter-spacing: var(--letter-spacing-wide);
  text-transform: uppercase;
  color: var(--color-text-muted);
}
.ok {
  color: var(--color-success-text);
  margin-top: var(--space-4);
  padding: var(--space-5);
  background: var(--color-success-bg);
  border-left: 3px solid var(--color-success-text);
  font-size: var(--font-size-base);
  line-height: var(--line-height-loose);
}

@media (max-width: 480px) {
  .app {
    padding: var(--space-5) var(--form-padding-x);
  }
}
</style>
